import debounce from "lodash-es/debounce";
import { useCallback, useEffect, useRef, useState } from "react";

import { ICategoryList } from "../types";

export const useListPageHandler = (categories: ICategoryList[]) => {
	const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
	const isClickScrolling = useRef(false);
	const clickScrollTimeoutRef = useRef<number | null>();
	const scrollIdleHandlerRef = useRef<(() => void) | null>(null);
	const productListRef = useRef<HTMLDivElement>(null);
	const categoryListRef = useRef<HTMLDivElement>(null);
	const productSectionRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

	// Effect to handle resetting the product list state
	useEffect(() => {
		const shouldReset = sessionStorage.getItem("resetProductList");

		if (shouldReset === "true") {
			// Reset scroll position to the top
			productListRef.current?.scrollTo(0, 0);

			// Reset active category to the first one if categories are available
			if (categories.length > 0) {
				setActiveCategoryId(categories[0].id);
			}

			// Clean up the flag from sessionStorage
			sessionStorage.removeItem("resetProductList");
		}
	}, [categories]);

	// Set first category as active when data loads for the first time
	useEffect(() => {
		if (categories.length > 0 && !activeCategoryId) {
			setActiveCategoryId(categories[0].id);
		}
	}, [categories, activeCategoryId]);

	const scrollCategoryIntoView = (categoryId: string) => {
		if (!categoryListRef.current) return;

		const categoryIndex = categories.findIndex((cat) => cat.id === categoryId);

		if (categoryIndex === -1) return;

		// Calculate scroll position for category list (vertical)
		const categoryItemHeight = 240; // 200px min-height + 20px margin + 20px padding
		const targetScrollTop = categoryIndex * categoryItemHeight;

		categoryListRef.current.scrollTo({
			top: targetScrollTop,
			behavior: "smooth",
		});
	};

	const handleScrollUpdateCategory = useCallback(
		debounce((categoryId: string) => {
			if (!isClickScrolling.current) {
				setActiveCategoryId((prevId) => {
					if (prevId !== categoryId) {
						// Scroll category list to show active category
						scrollCategoryIntoView(categoryId);

						return categoryId;
					}

					return prevId;
				});
			}
		}, 100),
		[categories]
	);

	const calculateTargetScrollTop = (categoryId: string): number | null => {
		const targetElement = productSectionRefs.current.get(categoryId);

		if (!targetElement || !productListRef.current) return null;

		const containerTop = productListRef.current.getBoundingClientRect().top;
		const targetTop = targetElement.getBoundingClientRect().top;
		const scrollTop = productListRef.current.scrollTop;

		return scrollTop + (targetTop - containerTop);
	};

	const scrollToPosition = (targetScrollTop: number) => {
		setTimeout(() => {
			const currentScrollTop = productListRef.current?.scrollTop || 0;

			productListRef.current?.scrollTo({
				top: Math.max(targetScrollTop - 40, 0),
				behavior: "smooth",
			});

			setTimeout(() => {
				if (productListRef.current) {
					const newScrollTop = productListRef.current.scrollTop;

					if (Math.abs(newScrollTop - currentScrollTop) < 10) {
						productListRef.current.scrollTop = targetScrollTop;
					}
				}
			}, 100);
		}, 50);
	};

	const resetClickScrolling = () => {
		if (clickScrollTimeoutRef.current) clearTimeout(clickScrollTimeoutRef.current);

		const container = productListRef.current;

		if (!container) {
			isClickScrolling.current = false;

			return;
		}

		// Remove any leftover handler from a prior click before attaching a new one.
		if (scrollIdleHandlerRef.current) {
			container.removeEventListener("scroll", scrollIdleHandlerRef.current);
			scrollIdleHandlerRef.current = null;
		}

		const finish = () => {
			isClickScrolling.current = false;
			if (scrollIdleHandlerRef.current) {
				container.removeEventListener("scroll", scrollIdleHandlerRef.current);
				scrollIdleHandlerRef.current = null;
			}
			if (clickScrollTimeoutRef.current) clearTimeout(clickScrollTimeoutRef.current);
			clickScrollTimeoutRef.current = null;
		};

		const onScrollIdle = () => {
			if (clickScrollTimeoutRef.current) clearTimeout(clickScrollTimeoutRef.current);
			clickScrollTimeoutRef.current = globalThis.setTimeout(finish, 200);
		};

		scrollIdleHandlerRef.current = onScrollIdle;
		container.addEventListener("scroll", onScrollIdle, { passive: true });

		// Initial schedule covers the case where scrollTo lands without firing scroll
		// (target already in view) so finish still runs.
		clickScrollTimeoutRef.current = globalThis.setTimeout(finish, 200);
	};

	const handleCategoryClick = useCallback(
		(categoryId: string) => {
			setActiveCategoryId(categoryId);
			isClickScrolling.current = true;

			const isFirstCategory = categories.length > 0 && categoryId === categories[0]?.id;
			const targetScrollTop = isFirstCategory ? 0 : calculateTargetScrollTop(categoryId);

			if (targetScrollTop !== null) {
				scrollToPosition(targetScrollTop);
				resetClickScrolling();
			} else {
				isClickScrolling.current = false;
			}
		},
		[categories]
	);

	const handleIntersectionObserver = useCallback(() => {
		if (!productListRef.current) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const visibleEntries = entries.filter((entry) => entry.isIntersecting);

				if (visibleEntries.length > 0) {
					const topMostEntry = visibleEntries.reduce((closest, current) => {
						return current.boundingClientRect.top < closest.boundingClientRect.top
							? current
							: closest;
					});

					const categoryId = topMostEntry.target.id.replace("category-", "");

					handleScrollUpdateCategory(categoryId);
				}
			},
			{
				root: productListRef.current,
				rootMargin: "-40% 0px -40% 0px",
				threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
			}
		);

		const handleScroll = () => {
			if (!productListRef.current || categories.length === 0) return;

			const scrollContainer = productListRef.current;
			const scrollTop = scrollContainer.scrollTop;
			const scrollHeight = scrollContainer.scrollHeight;
			const clientHeight = scrollContainer.clientHeight;

			if (scrollTop === 0) {
				handleScrollUpdateCategory(categories[0].id);

				return;
			}

			if (scrollTop + clientHeight >= scrollHeight) {
				handleScrollUpdateCategory(categories[categories.length - 1].id);

				return;
			}
		};

		// Observe all category sections
		productSectionRefs.current.forEach((element) => {
			if (element) {
				observer.observe(element);
			}
		});

		productListRef.current.addEventListener("scroll", handleScroll, { passive: true });

		return () => {
			observer.disconnect();
			if (productListRef.current) {
				productListRef.current.removeEventListener("scroll", handleScroll);
			}
		};
	}, [handleScrollUpdateCategory, categories]);

	useEffect(() => {
		const cleanup = handleIntersectionObserver();

		return cleanup;
	}, [handleIntersectionObserver]);

	return {
		productListRef,
		categoryListRef,
		productSectionRefs,
		handleScrollUpdateCategory,
		handleCategoryClick,
		isClickScrolling,
		activeCategoryId,
	};
};
